import { energyWord, EN_LOW_THRESHOLD, formatClock, formatHours } from "../format";
import type { ObservedView } from "../types";
import { Meter } from "./Meter";

/** Single-row HUD (19 §3). Only the stats Tier 2 makes visible; no KPIs, no previews. */
export function Hud({ view, onMenu, onHelp }: { view: ObservedView; onMenu: () => void; onHelp: () => void }) {
  const { clock, hud } = view;
  const en = energyWord(hud.en);
  const evening = view.diary?.eveningHours ?? 0;
  const hoursWords = `Hours this week: ${formatHours(hud.hoursThisWeek)} of ${formatHours(hud.plannedHours)} planned`
    + (evening > 0.05 ? `. ${formatHours(evening)} in the evenings and at weekends.` : "");
  return (
    <header className="hud" aria-label="Your week">
      <div className="hud-item hud-clock">
        <span className="hud-week">Week {clock.week} of {clock.totalWeeks}</span>
        <span className="hud-when">{formatClock(clock)}</span>
      </div>
      {/* Quick play (06 Quick play): marked for the whole run. */}
      {view.run.mode === "quick" && <div className="hud-item"><span className="mode-tag">Quick play</span></div>}
      {/* Each stat is written out in words for screen readers; the abbreviated visual form is hidden from them. */}
      <div className="hud-item" title={hoursWords}>
        <span className="visually-hidden">{hoursWords}</span>
        <span className="hud-label" aria-hidden="true">Hours</span>
        <span className="hud-value num" aria-hidden="true">{formatHours(hud.hoursThisWeek)} <span className="muted">/ {formatHours(hud.plannedHours)}</span></span>
      </div>
      <div className="hud-item">
        <span className="visually-hidden">Discretionary time remaining: {formatHours(hud.discretionaryRemaining)} of {formatHours(hud.discretionaryPlanned)}</span>
        <span className="hud-label" aria-hidden="true">Discretionary</span>
        <span className="hud-value num" aria-hidden="true">{formatHours(hud.discretionaryRemaining)} <span className="muted">of {formatHours(hud.discretionaryPlanned)}</span></span>
      </div>
      <Meter label="EN" value={hud.en} word={en} tone={hud.en < EN_LOW_THRESHOLD ? "low" : "en"} describe={`Energy: ${en}`} />
      <Meter label="PC" value={hud.pc} tone="pc" describe={`Political capital: ${hud.pc} out of 100`} />
      <div className="hud-item">
        <span className="visually-hidden">Delegation points: {hud.dp}</span>
        <span className="hud-label" aria-hidden="true">DP</span>
        <span className="hud-value num" aria-hidden="true">{hud.dp}</span>
      </div>
      <div className="hud-item">
        <span className="visually-hidden">Freedom points: {hud.fp}</span>
        <span className="hud-label" aria-hidden="true">FP</span>
        <span className="hud-value num fp" key={hud.fp} aria-hidden="true">{hud.fp}</span>
      </div>
      <div className="hud-actions">
        <button type="button" className="btn btn--icon" onClick={onHelp} aria-label="Controls help" title="Controls (?)">?</button>
        <button type="button" className="btn btn--icon" onClick={onMenu} aria-label="Menu and settings" title="Menu">≡</button>
      </div>
    </header>
  );
}
